import React, { Suspense, lazy } from 'react';
import { useUser } from '../context/UserContext';
import { useFirebaseAuthReady } from '../hooks/useFirebaseAuthReady';
import { useTheme } from '../context/ThemeProvider';
import { Routes, Route, Navigate } from 'react-router-dom';

// Lazy load all route components for code splitting
const Login = lazy(() => import('../app/login/page'));
const Signup = lazy(() => import('../app/signup/page'));
const EmployeeSignup = lazy(() => import('../app/employee-signup/[employeeRegistrationCode]/page'));
const ResetPassword = lazy(() => import('../app/reset-password/page'));
const ConfirmPassword = lazy(() => import('../app/confirm-password/page'));
const Home = lazy(() => import('../app/home/page'));
const Settings = lazy(() => import('../app/settings/page'));
const Document = lazy(() => import('../app/document/[documentId]/page'));
const User = lazy(() => import('../app/user/[userId]/page'));
const Company = lazy(() => import('../app/company/[companyId]/page'));
const Budget = lazy(() => import('../app/budget/[budgetId]/page'));
const Services = lazy(() => import('../app/services/page'));
const EligibleServices = lazy(() => import('../app/eligible-services/page'));
const Changelog = lazy(() => import('../app/changelog/page'));
const AI = lazy(() => import('../app/ai/page'));
const Controls = lazy(() => import('../app/controls/page'));
const CareIndex = lazy(() => import('../app/care-index/page'));
const Documentation = lazy(() => import('../app/documentation/page'));

interface AuthViewProps {
  children?: React.ReactNode;
}

export default function AuthView({ children }: AuthViewProps) {
  const { user, loading } = useUser();
  const isAuthReady = useFirebaseAuthReady();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Loading component for Suspense fallback
  const LoadingFallback = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '18px'
    }}>
      <img src={isDarkMode ? "/assets/graphics/morgen-loading-white.svg" : "/assets/graphics/morgen-loading.svg"} alt="Loading" width={120} height={120} />
    </div>
  );

  // Show loading spinner while Firebase auth is initializing
  if (!isAuthReady || loading) {
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/documentation/*" element={<Documentation />} />
        {!user ? (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/employee-signup" element={<EmployeeSignup />} />
            <Route path="/employee-signup/:employeeRegistrationCode" element={<EmployeeSignup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-password" element={<ConfirmPassword />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <>
            <Route path="/home" element={<Home />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/document/:documentId" element={<Document />} />
            <Route path="/user/:userId" element={<User />} />
            <Route path="/company/:companyId" element={<Company />} />
            <Route path="/budget/:budgetId" element={<Budget />} />
            <Route path="/services" element={<Services />} />
            <Route path="/eligible-services" element={<EligibleServices />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/ai" element={<AI />} />
            <Route path="/controls" element={<Controls />} />
            <Route path="/care-index/:companyId" element={<CareIndex />} />
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
}
